import * as React from 'react';
import { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context } from '@micro-frontend-react/core/lib/Context';

import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import {
  isHrcGerWitReviewed,
  isHrcLoaReviewed,
  normalizePbtFlags,
  mapApiStatusToUiStatus,
} from '../../../Interfaces/IPBTCase';
import { PBTCaseActionService } from '../../../Services/PBTCaseActionService';
import type { PauseCaseRequest, CancelCaseRequest } from '../../../Services/PBTCaseActionService';

// Success banners are transient confirmations, so auto-dismiss them after a short delay instead of
// leaving them on screen until the next page reload. Errors — and successes that carry warnings the
// user should read — are left for manual dismissal via the banner's close (X) button.
const SUCCESS_AUTO_DISMISS_MS = 5000;

/**
 * Hook that provides pause/resume/cancel dialog state and API interaction
 * for a selected PBT case.
 */
export function useCaseActions() {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);

  const serviceRef = React.useRef<PBTCaseActionService | null>(null);
  if (!serviceRef.current) {
    serviceRef.current = new PBTCaseActionService(() =>
      authClient.acquireToken(__SCOPES__) as Promise<string>
    );
  }

  const [pauseDialogOpen, setPauseDialogOpen] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = React.useState(false);
  const [targetCase, setTargetCase] = React.useState<IPBTCase | null>(null);
  const [updatedCase, setUpdatedCase] = React.useState<IPBTCase | null>(null);
  const [actionResult, setActionResult] = React.useState<{ success: boolean; message: string; warnings?: string[] } | null>(null);
  const clearUpdatedCase = React.useCallback(() => setUpdatedCase(null), []);

  // Auto-dismiss a plain success banner after a short delay (see SUCCESS_AUTO_DISMISS_MS). A new
  // actionResult replaces the object identity, so this effect re-runs and clears any prior timer.
  React.useEffect(() => {
    if (!actionResult?.success) return undefined;
    if (actionResult.warnings && actionResult.warnings.length > 0) return undefined;
    const timer = setTimeout(() => setActionResult(null), SUCCESS_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [actionResult]);

  // Merge the case returned by the API onto the known case so callers can
  // reflect the new status/available actions without waiting for a refresh.
  const mergeUpdatedCase = React.useCallback((base: IPBTCase, result: { case?: Record<string, unknown> }): IPBTCase => {
    const merged = { ...base, ...(result.case as Partial<IPBTCase> | undefined) };
    // The API response serializes flags as a comma-separated string / bit-field, which would
    // otherwise overwrite the mapped PBTFlag[] and break flag rendering. Re-normalize to an array.
    const casePayload = result.case as Record<string, unknown> | undefined;
    const flagsValue =
      casePayload && Object.prototype.hasOwnProperty.call(casePayload, 'flags')
        ? casePayload.flags
        : base.flags;
    merged.flags = normalizePbtFlags(flagsValue);
    merged.hrcLoaReviewed =
      casePayload && Object.prototype.hasOwnProperty.call(casePayload, 'flags')
        ? isHrcLoaReviewed(flagsValue)
        : base.hrcLoaReviewed;
    merged.hrcGerWitReviewed =
      casePayload && Object.prototype.hasOwnProperty.call(casePayload, 'flags')
        ? isHrcGerWitReviewed(flagsValue)
        : base.hrcGerWitReviewed;
    // The API serializes status as the raw enum name (e.g. "AwaitingAttestation"/"Complete"),
    // which would overwrite the UI display value ("Date & Attestation Pending"/"Complete") and break
    // the stepper derivation (which switches on the display strings), leaving a resumed case stuck
    // at step 1. Normalize back to the display value so the action bar toggles live (e.g.
    // Resume -> Pause) without a page refresh. Idempotent for already-mapped values.
    merged.status = mapApiStatusToUiStatus(merged.status) as IPBTCase['status'];
    return merged;
  }, []);

  const openPauseDialog = React.useCallback((pbtCase: IPBTCase) => {
    setTargetCase(pbtCase);
    setPauseDialogOpen(true);
    setActionResult(null);
  }, []);

  const openCancelDialog = React.useCallback((pbtCase: IPBTCase) => {
    setTargetCase(pbtCase);
    setCancelDialogOpen(true);
    setActionResult(null);
  }, []);

  const openResumeDialog = React.useCallback((pbtCase: IPBTCase) => {
    setTargetCase(pbtCase);
    setResumeDialogOpen(true);
    setActionResult(null);
  }, []);

  const closePauseDialog = React.useCallback(() => {
    setPauseDialogOpen(false);
    setTargetCase(null);
  }, []);

  const closeCancelDialog = React.useCallback(() => {
    setCancelDialogOpen(false);
    setTargetCase(null);
  }, []);

  const closeResumeDialog = React.useCallback(() => {
    setResumeDialogOpen(false);
    setTargetCase(null);
  }, []);

  const handlePauseConfirm = React.useCallback(async (reason: string, reasonText?: string) => {
    if (!targetCase || !serviceRef.current) return;
    const request: PauseCaseRequest = { reason, reasonText };
    const result = await serviceRef.current.pauseCase(targetCase.id, request);
    setUpdatedCase(mergeUpdatedCase(targetCase, result));
    setActionResult({ success: true, message: 'Case paused successfully.', warnings: result.warnings });
    closePauseDialog();
  }, [targetCase, closePauseDialog, mergeUpdatedCase]);

  const resumeCase = React.useCallback(async (pbtCase: IPBTCase) => {
    if (!serviceRef.current) return;
    try {
      const result = await serviceRef.current.resumeCase(pbtCase.id);
      setUpdatedCase(mergeUpdatedCase(pbtCase, result));
      setActionResult({ success: true, message: 'Case resumed successfully.', warnings: result.warnings });
    } catch (err) {
      setActionResult({ success: false, message: (err as Error).message || 'Failed to resume case.' });
    }
  }, [mergeUpdatedCase]);

  // Dialog-driven resume: rethrows on failure so the confirmation dialog can
  // surface the error inline; the parent closes the dialog on success.
  const handleResumeConfirm = React.useCallback(async () => {
    if (!targetCase || !serviceRef.current) return;
    const result = await serviceRef.current.resumeCase(targetCase.id);
    setUpdatedCase(mergeUpdatedCase(targetCase, result));
    setActionResult({ success: true, message: 'Case resumed successfully.', warnings: result.warnings });
    closeResumeDialog();
  }, [targetCase, closeResumeDialog, mergeUpdatedCase]);

  const handleCancelConfirm = React.useCallback(async (reason: string, reasonText?: string) => {
    if (!targetCase || !serviceRef.current) return;
    const request: CancelCaseRequest = { reason, reasonText };
    const result = await serviceRef.current.cancelCase(targetCase.id, request);
    setUpdatedCase(mergeUpdatedCase(targetCase, result));
    setActionResult({ success: true, message: 'Case cancelled successfully.', warnings: result.warnings });
    closeCancelDialog();
  }, [targetCase, closeCancelDialog, mergeUpdatedCase]);

  return {
    pauseDialogOpen,
    cancelDialogOpen,
    resumeDialogOpen,
    targetCase,
    updatedCase,
    actionResult,
    openPauseDialog,
    openCancelDialog,
    openResumeDialog,
    closePauseDialog,
    closeCancelDialog,
    closeResumeDialog,
    handlePauseConfirm,
    handleCancelConfirm,
    handleResumeConfirm,
    resumeCase,
    clearActionResult: () => setActionResult(null),
    clearUpdatedCase,
  };
}
